/**
 * The sales desks, as the SPA knows them.
 *
 * Mirrors `SALES_TEAM_SLUGS` on the API. Duplicated across the boundary rather
 * than shared, like every other slug these pages know: `apps/api` cannot be
 * imported from here, and the server validates against its own copy anyway --
 * this list decides what is *offered*, never what is allowed.
 *
 * Three slugs and not two. A desk that has never split sales in half seeds one
 * team called "Sales", and leaving it out would empty every picker built on
 * this list on exactly the desks that need it least explained.
 */
export const SALES_TEAM_SLUGS = ["internal-sales", "external-sales", "sales"];

/** Somebody the settings pages know about, which is all this needs of a user. */
interface TeamLike {
  slug: string;
  members: { id: number; name: string }[];
}

export interface AccountManagers {
  people: { id: number; name: string }[];
  /**
   * True when the desks are empty and everybody is being offered instead.
   *
   * The caller says so on screen rather than quietly behaving like the old
   * picker -- somebody who has just been told account managers come from the
   * sales desks needs to know why this one is listing the whole company.
   */
  everybody: boolean;
}

/**
 * Everybody on a sales desk, once, by name.
 *
 * `team_members` is many-to-many and somebody genuinely can be in both desks --
 * the API says so in two places -- so the ids are folded through a map rather
 * than concatenated, or a picker would list them twice.
 *
 * Falls back to every user when the desks between them have nobody on them,
 * which covers both a desk that has not created the teams and one that created
 * them and never filled them in. A picker that offers nobody cannot be used at
 * all, and "you may not set an account manager until an admin fixes Settings"
 * is a worse answer than the one this replaced. It is not silent about it: see
 * `everybody`.
 */
export function salesTeamMembers<T extends TeamLike>(
  teams: readonly T[],
  everyone: readonly { id: number; name: string }[],
): AccountManagers {
  const byId = new Map<number, { id: number; name: string }>();

  for (const team of teams) {
    if (!SALES_TEAM_SLUGS.includes(team.slug)) continue;
    for (const member of team.members) byId.set(member.id, member);
  }

  if (byId.size === 0) return { people: [...everyone], everybody: true };

  return {
    people: [...byId.values()].sort((a, b) => a.name.localeCompare(b.name)),
    everybody: false,
  };
}
